import React, { useState, useEffect, useMemo } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
} from 'recharts';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import { api } from '../api/client.js';
import { formatCOPAllowZero } from '../utils/money.js';
import './Indicators.css';

const YEAR_LINE_COLORS = ['#2196f3', '#f44336', '#4caf50', '#9c27b0', '#ff9800', '#795548'];
const CAUSE_COLORS = ['#5c6bc0', '#26a69a', '#ef5350', '#ffa726', '#ab47bc', '#66bb6a', '#8d6e63', '#42a5f5'];

function isDemoRetirementRow(evt) {
    const code = String(evt.employee_code || '').toUpperCase();
    const name = String(evt.employee_name || '').toLowerCase();
    return code.startsWith('CID_OFF') || name.includes('laura meza') || name.includes('baja demo');
}

const fmt = (n) =>
    n != null
        ? `$${Number(n).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        : '$0';

function Indicators() {
    const { user } = useAuth();
    const [dash, setDash] = useState(null);
    const [retirementHistory, setRetirementHistory] = useState([]);
    const [loadingRotation, setLoadingRotation] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(true);

    const { tenantId } = useTenantScope();

    useEffect(() => {
        loadAll();
    }, [tenantId]);

    async function loadAll() {
        setLoadingRotation(true);
        setLoadingHistory(true);
        try {
            const [indRes, histRes] = await Promise.all([
                api.getIndicatorsDashboard(),
                api.getRetirementHistory(tenantId),
            ]);
            setDash(indRes.data || indRes);
            setRetirementHistory((histRes.data || []).filter((evt) => !isDemoRetirementRow(evt)));
        } catch (err) {
            console.error('Error loading indicators:', err);
        } finally {
            setLoadingRotation(false);
            setLoadingHistory(false);
        }
    }

    const totalCID = retirementHistory.reduce((sum, e) => sum + (e.total_impact || 0), 0);

    const costByArea = dash?.costByArea || [];
    const causesByMonth = dash?.causesByMonth || [];
    const causesYtd = dash?.causesYtd || [];
    const byEncargadoYtd = dash?.byEncargadoYtd || [];
    const chartYears = dash?.chartYears || [];
    const rotationRateChart = dash?.rotationRateChart || [];

    const topReasons = useMemo(() => causesYtd.slice(0, 8).map((c) => c.reason), [causesYtd]);

    const causesChartData = useMemo(() => {
        if (!causesByMonth.length) return [];
        return causesByMonth.map((m) => {
            const row = { month: m.monthLabel };
            for (const reason of topReasons) {
                row[reason] = m.causes[reason] || 0;
            }
            return row;
        });
    }, [causesByMonth, topReasons]);

    const rotationTotals = useMemo(() => {
        const retiros = costByArea.reduce((s, r) => s + r.retirosMes, 0);
        const personas = costByArea.reduce((s, r) => s + r.personas, 0);
        const peso =
            dash?.totals?.retirosMes && dash?.headcount
                ? ((dash.totals.retirosMes / dash.headcount) * 100).toFixed(2)
                : personas > 0
                  ? ((retiros / personas) * 100).toFixed(2)
                  : '0';
        return { retiros, personas, peso };
    }, [costByArea, dash]);

    const periodLabel =
        dash?.periodLabel ||
        new Date().toLocaleDateString('es-CO', { month: 'long', year: 'numeric' }).toUpperCase();

    return (
        <div className="indicators-container">
            <div className="indicators-header">
                <div>
                    <h1>Dashboard de Indicadores</h1>
                    {user?.tenantName && (
                        <p className="indicators-tenant">Empresa: {user.tenantName}</p>
                    )}
                </div>
                <div className="indicators-date">Corte: {periodLabel}</div>
            </div>

            <div className="indicators-grid">
                {/* 0. Historial de retiros — registro en vivo (empresa actual) */}
                <div className="indicator-card" style={{ borderLeft: '5px solid #e53935' }}>
                    <h2>📋 HISTORIAL DE RETIROS — REGISTRO EN VIVO</h2>

                    <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                        <div
                            style={{
                                flex: '1',
                                minWidth: '180px',
                                background: 'linear-gradient(135deg, #e53935, #c62828)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '1.2rem',
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: '2.2rem', fontWeight: 800 }}>{retirementHistory.length}</div>
                            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Total Retiros</div>
                        </div>
                        <div
                            style={{
                                flex: '1',
                                minWidth: '180px',
                                background: 'linear-gradient(135deg, #ff6f00, #e65100)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '1.2rem',
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: '2.2rem', fontWeight: 800 }}>{fmt(totalCID)}</div>
                            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>CID Acumulado</div>
                        </div>
                        <div
                            style={{
                                flex: '1',
                                minWidth: '180px',
                                background: 'linear-gradient(135deg, #1565c0, #0d47a1)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '1.2rem',
                                textAlign: 'center',
                            }}
                        >
                            <div style={{ fontSize: '2.2rem', fontWeight: 800 }}>
                                {retirementHistory.length > 0 ? fmt(totalCID / retirementHistory.length) : '$0'}
                            </div>
                            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Promedio CID / Retiro</div>
                        </div>
                    </div>

                    {loadingHistory ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Cargando historial...</div>
                    ) : retirementHistory.length === 0 ? (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '2rem',
                                color: '#999',
                                background: '#f9f9f9',
                                borderRadius: '8px',
                            }}
                        >
                            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>📭 Sin retiros registrados</p>
                            <p style={{ fontSize: '0.85rem' }}>
                                Los retiros procesados desde la Base de Personal aparecerán aquí automáticamente.
                            </p>
                        </div>
                    ) : (
                        <div className="data-table-container">
                            <table className="indicator-table">
                                <thead>
                                    <tr>
                                        <th>FECHA</th>
                                        <th>COLABORADOR</th>
                                        <th>CÓDIGO</th>
                                        <th>ÁREA</th>
                                        <th>CAUSAL DE RETIRO</th>
                                        <th>COSTOS DIRECTOS</th>
                                        <th>PROD. PERDIDA</th>
                                        <th>FRICCIÓN SIST.</th>
                                        <th style={{ background: '#c62828' }}>CID TOTAL</th>
                                        <th>OBSERVACIONES</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {retirementHistory.map((evt) => (
                                        <tr key={evt.id}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{evt.event_date}</td>
                                            <td style={{ fontWeight: 600 }}>{evt.employee_name}</td>
                                            <td>{evt.employee_code}</td>
                                            <td>{evt.area_name}</td>
                                            <td>
                                                <span
                                                    style={{
                                                        background: '#fff3e0',
                                                        color: '#e65100',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {evt.retirement_reason || 'Sin especificar'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{fmt(evt.direct_cost)}</td>
                                            <td style={{ textAlign: 'right' }}>{fmt(evt.productivity_lost)}</td>
                                            <td style={{ textAlign: 'right' }}>{fmt(evt.systemic_friction)}</td>
                                            <td
                                                style={{
                                                    textAlign: 'right',
                                                    fontWeight: 800,
                                                    color: '#c62828',
                                                    background: '#ffebee',
                                                }}
                                            >
                                                {fmt(evt.total_impact)}
                                            </td>
                                            <td style={{ maxWidth: '200px', fontSize: '0.8rem', color: '#666' }}>
                                                {evt.comments || evt.reason || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'right' }}>
                                            TOTALES
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {fmt(retirementHistory.reduce((s, e) => s + (e.direct_cost || 0), 0))}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {fmt(retirementHistory.reduce((s, e) => s + (e.productivity_lost || 0), 0))}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {fmt(retirementHistory.reduce((s, e) => s + (e.systemic_friction || 0), 0))}
                                        </td>
                                        <td style={{ textAlign: 'right', fontSize: '1.1rem' }}>{fmt(totalCID)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {/* 1. Rotación por área y encargado — datos empresa */}
                <div className="indicator-card">
                    <h2>PESO DE ROTACIÓN POR ENCARGADOS Y ÁREA</h2>
                    <p className="indicator-card-hint">
                        Datos de tu empresa · ordenado por CID del mes en curso.
                    </p>
                    {loadingRotation ? (
                        <p className="text-muted text-center py-lg">Cargando rotación…</p>
                    ) : costByArea.length === 0 ? (
                        <p className="text-muted text-center py-lg">
                            Sin retiros registrados en el mes. Procesa bajas desde Base de personal.
                        </p>
                    ) : (
                        <div className="data-table-container">
                            <table className="indicator-table">
                                <thead>
                                    <tr>
                                        <th>ENCARGADO</th>
                                        <th>ÁREA</th>
                                        <th>No. RETIROS (MES)</th>
                                        <th>No. PERSONAS</th>
                                        <th>PESO % MES</th>
                                        <th>CID MES</th>
                                        <th>CID AÑO</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {costByArea.map((row) => (
                                        <tr key={row.areaId}>
                                            <td>{row.encargado}</td>
                                            <td>{row.areaName}</td>
                                            <td style={{ textAlign: 'center' }}>{row.retirosMes}</td>
                                            <td style={{ textAlign: 'center' }}>{row.personas}</td>
                                            <td className="highlight-cell">{row.pesoMesPct}%</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                                {formatCOPAllowZero(row.cidMes)}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{formatCOPAllowZero(row.cidYtd)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={2}>TOTAL</td>
                                        <td style={{ textAlign: 'center' }}>{rotationTotals.retiros}</td>
                                        <td style={{ textAlign: 'center' }}>{rotationTotals.personas}</td>
                                        <td className="highlight-cell">{rotationTotals.peso}%</td>
                                        <td style={{ textAlign: 'right' }}>
                                            {formatCOPAllowZero(dash?.totals?.cidMes)}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {formatCOPAllowZero(dash?.totals?.cidYtd)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {/* 2. Causas de rotación — comparativo mes a mes */}
                <div className="indicator-card">
                    <h2>CAUSAS DE ROTACIÓN — COMPARATIVO MES A MES</h2>
                    <p className="indicator-card-hint">Últimos 12 meses · causales registradas en retiros.</p>
                    {loadingRotation ? (
                        <p className="text-muted text-center py-lg">Cargando causales…</p>
                    ) : causesChartData.length === 0 ? (
                        <p className="text-muted text-center py-lg">Sin causales en el período.</p>
                    ) : (
                        <>
                            <div className="chart-container" style={{ height: '300px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={causesChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="month"
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                            interval={0}
                                            tick={{ fontSize: 10 }}
                                        />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip />
                                        <Legend verticalAlign="top" wrapperStyle={{ fontSize: '0.75rem' }} />
                                        {topReasons.map((reason, i) => (
                                            <Bar
                                                key={reason}
                                                dataKey={reason}
                                                stackId="causas"
                                                fill={CAUSE_COLORS[i % CAUSE_COLORS.length]}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="data-table-container" style={{ marginTop: '2rem' }}>
                                <table className="indicator-table">
                                    <thead>
                                        <tr>
                                            <th>CAUSAL</th>
                                            {causesByMonth.map((m) => (
                                                <th key={m.ym}>{m.monthLabel}</th>
                                            ))}
                                            <th>TOTAL {dash?.year}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {causesYtd.map((row) => (
                                            <tr key={row.reason}>
                                                <td>{row.reason}</td>
                                                {causesByMonth.map((m) => (
                                                    <td key={m.ym} style={{ textAlign: 'center' }}>
                                                        {m.causes[row.reason] || 0}
                                                    </td>
                                                ))}
                                                <td className="highlight-cell" style={{ textAlign: 'center' }}>
                                                    {row.eventos}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* 3. Acumulado del año por encargado */}
                <div className="indicator-card">
                    <h2>ACUMULADO DEL AÑO POR ENCARGADO</h2>
                    <p className="indicator-card-hint">
                        Retiros y CID acumulado {dash?.year || new Date().getFullYear()} por líder y área.
                    </p>
                    {loadingRotation ? (
                        <p className="text-muted text-center py-lg">Cargando…</p>
                    ) : byEncargadoYtd.length === 0 ? (
                        <p className="text-muted text-center py-lg">Sin retiros acumulados en el año.</p>
                    ) : (
                        <div className="data-table-container">
                            <table className="indicator-table">
                                <thead>
                                    <tr>
                                        <th>ENCARGADO</th>
                                        <th>ÁREA</th>
                                        <th>RETIROS (AÑO)</th>
                                        <th>CID ACUMULADO</th>
                                        <th>PESO % CID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byEncargadoYtd.map((row, idx) => (
                                        <tr key={`${row.encargado}-${row.areaName}-${idx}`}>
                                            <td>{row.encargado}</td>
                                            <td>{row.areaName}</td>
                                            <td style={{ textAlign: 'center' }}>{row.retiros}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                                {formatCOPAllowZero(row.cidYtd)}
                                            </td>
                                            <td className="highlight-cell">{row.pesoPct}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* 4. Gráfica interanual — datos empresa */}
                <div className="indicator-card">
                    <h2>INDICADOR DE ROTACIÓN RETIROS TOTALES</h2>
                    <p className="indicator-card-hint">
                        Tasa mensual = retiros del mes ÷ personas activas ({dash?.headcount ?? '—'}).
                    </p>
                    {loadingRotation ? (
                        <p className="text-muted text-center py-lg">Cargando gráfica…</p>
                    ) : chartYears.length === 0 ? (
                        <p className="text-muted text-center py-lg">
                            Aún no hay historial de retiros para la curva interanual.
                        </p>
                    ) : (
                        <>
                            <div className="chart-container" style={{ minHeight: '400px' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={rotationRateChart} margin={{ top: 20, right: 30, left: 20, bottom: 48 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                                        <YAxis tickFormatter={(val) => `${val}%`} />
                                        <Tooltip formatter={(value) => `${value}%`} />
                                        <Legend />
                                        {chartYears.map((y, i) => (
                                            <Line
                                                key={y}
                                                type="monotone"
                                                dataKey={`y${y}`}
                                                name={String(y)}
                                                stroke={YEAR_LINE_COLORS[i % YEAR_LINE_COLORS.length]}
                                                strokeWidth={i === chartYears.length - 1 ? 3 : 2}
                                                dot={{ r: i === chartYears.length - 1 ? 5 : 4 }}
                                                connectNulls
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="indicator-footnote">
                                Comparativo interanual calculado con los retiros registrados en ALINEA para tu
                                empresa.
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Indicators;
